import { useEffect, useState } from 'react';
import { RUNNING_VERSION, isVersionCheckEnabled, newerDeployedVersion } from '../utils/appVersion';

export const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
/** Two tab switches within this window do not fetch twice. */
export const VERSION_CHECK_THROTTLE_MS = 60 * 1000;

/**
 * Polls `/version.json` (no-store, cache-busted) every 5 minutes and whenever the tab becomes visible
 * again, and returns the deployed version once it differs from the running one (then stops polling).
 * A no-op for a dev build; network errors are ignored (the next tick retries).
 */
export function useNewVersionAvailable(runningVersion: string = RUNNING_VERSION): string | null {
  const [available, setAvailable] = useState<string | null>(null);

  useEffect(() => {
    if (!isVersionCheckEnabled(runningVersion)) {
      return undefined;
    }
    let stopped = false;
    let lastCheck = -Infinity;

    const stop = () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };

    const check = async () => {
      const now = Date.now();
      if (stopped || now - lastCheck < VERSION_CHECK_THROTTLE_MS) {
        return;
      }
      lastCheck = now;
      try {
        const response = await fetch(`/version.json?t=${now}`, { cache: 'no-store' });
        if (!response.ok) {
          return;
        }
        const deployed = newerDeployedVersion(runningVersion, await response.json());
        if (deployed !== null && !stopped) {
          setAvailable(deployed);
          stop();
        }
      } catch {
        // Offline, or a deploy in progress: the next tick tries again.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void check();
      }
    };

    const timer = window.setInterval(() => void check(), VERSION_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibility);
    void check();
    return stop;
  }, [runningVersion]);

  return available;
}
